import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FORMAT_TEXT_COMMAND, $getSelection, $isRangeSelection } from 'lexical';
import {
  getMostCommonFontFamilyInSelection,
  getFontFamilyFromDOM,
} from '../Views/Editor/Components/Plugins/FontFamily/FontFamilyHelpers';
import { getMostCommonFontSizeInSelection } from '../Views/Editor/Components/Plugins/FontSize/FontSizeHelpers';
import {
  getMostCommonColorInSelection,
  getColorFromDOM,
} from '../Views/Editor/Components/Plugins/Color/ColorHelpers';
import { getMostCommonHighlightColorInSelection } from '../Views/Editor/Components/Plugins/Highlight/HighlightHelpers';

const EditorContext = createContext();

export const useEditorContext = () => {
  const context = useContext(EditorContext);
  if (!context) {
    throw new Error('useEditorContext must be used within an EditorProvider');
  }
  return context;
};

export const EditorProvider = ({ children }) => {
  const [activeEditor, setActiveEditor] = useState(null);
  const [allEditors, setAllEditors] = useState(new Map());
  const [formattingState, setFormattingState] = useState({
    fontFamily: 'Arial',
    fontSize: '12px',
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    fontColor: '#000000',
    // Null = no highlight in selection; defaulting to yellow made the toolbar look "already on".
    highlightColor: null,
    textAlign: 'left',
    lineSpacing: 1,
    listType: 'none',
    indentLevel: 0,
  });

  const syncFormattingWithEditor = useCallback((editor) => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        // Sync formatting for both selected text and cursor position
        const currentFormatting = {
          bold: selection.hasFormat('bold'),
          italic: selection.hasFormat('italic'),
          underline: selection.hasFormat('underline'),
          strikethrough: selection.hasFormat('strikethrough'),
        };

        // Get font family from selection (works for both selected text and cursor position)
        let fontFamily = getMostCommonFontFamilyInSelection();
        if (!fontFamily) {
          fontFamily = getFontFamilyFromDOM();
        }
        if (fontFamily) {
          currentFormatting.fontFamily = fontFamily;
        }

        // Get font size from selection
        const fontSize = getMostCommonFontSizeInSelection();
        if (fontSize) {
          currentFormatting.fontSize = fontSize;
        }

        // Get text color from selection
        let fontColor = getMostCommonColorInSelection();

        // If no color found in Lexical nodes, try DOM inspection
        if (!fontColor) {
          fontColor = getColorFromDOM();
        }

        if (fontColor) {
          currentFormatting.fontColor = fontColor;
        }

        // Get highlight color from selection (StyledTextNode + legacy HighlightNode)
        const highlightColor = getMostCommonHighlightColorInSelection();
        currentFormatting.highlightColor = highlightColor ?? null;

        setFormattingState((prev) => ({ ...prev, ...currentFormatting }));
      }
    });
  }, []);

  const applyCurrentFormatting = useCallback(
    (editor) => {
      // Apply formatting with error handling
      const applyFormat = (formatType) => {
        try {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, formatType);
        } catch (error) {
          // Gracefully handle readonly property errors
          console.warn(
            `Format command for ${formatType} failed (this is expected and safe):`,
            error.message
          );
        }
      };

      if (formattingState.bold) {
        applyFormat('bold');
      }
      if (formattingState.italic) {
        applyFormat('italic');
      }
      if (formattingState.underline) {
        applyFormat('underline');
      }
      if (formattingState.strikethrough) {
        applyFormat('strikethrough');
      }
    },
    [formattingState]
  );

  const registerEditor = useCallback(
    (editor, editorId) => {
      // Store the editor in the map
      setAllEditors((prev) => new Map(prev.set(editorId, { editor, editorId })));
      setActiveEditor({ editor, editorId });

      // Apply any active formatting when an editor becomes active
      // Only apply if there's actually formatting to apply
      if (
        formattingState.bold ||
        formattingState.italic ||
        formattingState.underline ||
        formattingState.strikethrough
      ) {
        applyCurrentFormatting(editor);
      }
    },
    [applyCurrentFormatting, formattingState]
  );

  const unregisterEditor = useCallback((editorId) => {
    setAllEditors((prev) => {
      const newMap = new Map(prev);
      newMap.delete(editorId);
      return newMap;
    });
    setActiveEditor((current) => {
      if (current?.editorId === editorId) {
        return null;
      }
      return current;
    });
  }, []);

  const updateFormatting = useCallback(
    (formattingUpdates) => {
      setFormattingState((prev) => ({ ...prev, ...formattingUpdates }));

      if (activeEditor?.editor) {
        // Apply formatting directly to selected text or cursor position
        requestAnimationFrame(() => {
          const applyFormat = (formatType) => {
            try {
              activeEditor.editor.dispatchCommand(FORMAT_TEXT_COMMAND, formatType);
            } catch (error) {
              // Gracefully handle readonly property errors
              console.warn(
                `Format command for ${formatType} failed (this is expected and safe):`,
                error.message
              );
            }
          };

          // Apply formatting based on what was toggled
          if (formattingUpdates.bold !== undefined) {
            applyFormat('bold');
          }
          if (formattingUpdates.italic !== undefined) {
            applyFormat('italic');
          }
          if (formattingUpdates.underline !== undefined) {
            applyFormat('underline');
          }
          if (formattingUpdates.strikethrough !== undefined) {
            applyFormat('strikethrough');
          }
          // Highlight is applied via HIGHLIGHT_COMMAND from ActionBar only (avoid double dispatch).
        });
      }
    },
    [activeEditor]
  );

  // Handle keyboard shortcuts for formatting
  const handleKeyDown = useCallback(
    (event) => {
      // Only handle shortcuts when an editor is active
      if (!activeEditor?.editor) return;

      // Check for Ctrl+B (Bold)
      if (event.ctrlKey && event.key === 'b') {
        event.preventDefault();
        updateFormatting({ bold: !formattingState.bold });
      }
      // Check for Ctrl+I (Italic)
      else if (event.ctrlKey && event.key === 'i') {
        event.preventDefault();
        updateFormatting({ italic: !formattingState.italic });
      }
      // Check for Ctrl+U (Underline)
      else if (event.ctrlKey && event.key === 'u') {
        event.preventDefault();
        updateFormatting({ underline: !formattingState.underline });
      }
      // Check for Ctrl+Shift+S (Strikethrough)
      else if (event.ctrlKey && event.shiftKey && event.key === 'S') {
        event.preventDefault();
        updateFormatting({ strikethrough: !formattingState.strikethrough });
      }
    },
    [activeEditor, formattingState, updateFormatting]
  );

  // Add global keyboard event listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const value = useMemo(
    () => ({
      activeEditor,
      allEditors,
      formattingState,
      registerEditor,
      unregisterEditor,
      updateFormatting,
      applyCurrentFormatting,
      syncFormattingWithEditor,
    }),
    [
      activeEditor,
      allEditors,
      formattingState,
      registerEditor,
      unregisterEditor,
      updateFormatting,
      applyCurrentFormatting,
      syncFormattingWithEditor,
    ]
  );

  return <EditorContext.Provider value={value}>{children}</EditorContext.Provider>;
};

EditorProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
